import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAiConversations,
  createAiConversation,
  getAiConversationMessages,
  streamAiConversationMessage,
} from "@/data";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Loader2, MessageSquarePlus, Send, Sparkles, Wrench } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { tr, enUS } from "date-fns/locale";

export function RituelyAiScreen() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [message, setMessage] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [streamingTool, setStreamingTool] = useState(null);
  const bottomRef = useRef(null);

  const {
    data: conversationsResponse,
    isLoading: isConversationsLoading,
    isFetching: isConversationsFetching,
  } = useQuery({
    queryKey: ["ai-conversations"],
    queryFn: getAiConversations,
  });

  const conversations = conversationsResponse?.data || [];

  useEffect(() => {
    if (!selectedConversationId && conversations.length > 0) {
      setSelectedConversationId(conversations[0].id);
    }
  }, [selectedConversationId, conversations]);

  const {
    data: messagesResponse,
    isLoading: isMessagesLoading,
    isFetching: isMessagesFetching,
  } = useQuery({
    queryKey: ["ai-messages", selectedConversationId],
    queryFn: () => getAiConversationMessages(selectedConversationId),
    enabled: Boolean(selectedConversationId),
    refetchInterval: isStreaming ? false : 8000, // Disable polling while streaming
  });

  const messages = useMemo(() => messagesResponse?.data || [], [messagesResponse]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, streamingContent, streamingTool]);

  const createConversationMutation = useMutation({
    mutationFn: createAiConversation,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["ai-conversations"] });
      const newConversation = response?.data;
      if (newConversation?.id) {
        setSelectedConversationId(newConversation.id);
      }
    },
  });

  const handleCreateConversation = () => {
    createConversationMutation.mutate({});
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    if (!selectedConversationId || !message.trim()) return;

    const currentMessage = message.trim();
    setMessage("");
    setIsStreaming(true);
    setStreamingContent("");
    setStreamingTool(null);

    await streamAiConversationMessage(
      selectedConversationId,
      currentMessage,
      (data) => {
        if (data.type === "user_message_saved") {
          queryClient.invalidateQueries({
            queryKey: ["ai-messages", selectedConversationId],
          });
        }
        if (data.type === "content") {
          setStreamingContent((prev) => prev + data.content);
        }
        if (data.type === "tool_start") {
          setStreamingTool(data.tool);
        }
        if (data.type === "tool_result") {
          setStreamingTool(null);
        }
      },
      () => {
        setIsStreaming(false);
        setStreamingContent("");
        setStreamingTool(null);
        queryClient.invalidateQueries({
          queryKey: ["ai-messages", selectedConversationId],
        });
        queryClient.invalidateQueries({ queryKey: ["ai-conversations"] });
      },
      (error) => {
        console.error(error);
        setIsStreaming(false);
        // Optionally show toast here
      }
    );
  };

  const activeConversation = conversations.find((c) => c.id === selectedConversationId);

  return (
    <AppLayout
      title={t("aiChat.title")}
      description={t("aiChat.subtitle")}
      actions={[
        {
          name: t("aiChat.newChat"),
          icon: <MessageSquarePlus className="h-4 w-4" />,
          onClick: handleCreateConversation,
          disabled: createConversationMutation.isPending,
        },
      ]}
    >
      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <Card className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">{t("aiChat.conversations")}</p>
              <p className="text-xs text-muted-foreground">
                {isConversationsFetching
                  ? t("aiChat.updating")
                  : t("aiChat.activeSessions", { count: conversations.length })}
              </p>
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={handleCreateConversation}
              disabled={createConversationMutation.isPending}
            >
              {createConversationMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
            </Button>
          </div>

          <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
            {isConversationsLoading && (
              <div className="text-sm text-muted-foreground">{t("aiChat.loadingConversations")}</div>
            )}

            {!isConversationsLoading && conversations.length === 0 && (
              <div className="text-sm text-muted-foreground">
                {t("aiChat.noConversations")}
              </div>
            )}

            {conversations.map((conversation) => (
              <button
                key={conversation.id}
                onClick={() => setSelectedConversationId(conversation.id)}
                className={cn(
                  "w-full rounded-xl border p-3 text-left transition hover:border-primary/50",
                  conversation.id === selectedConversationId
                    ? "border-primary bg-primary/10"
                    : "border-border"
                )}
              >
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <span>
                    {conversation.lastMessageAt
                      ? formatDistanceToNow(new Date(conversation.lastMessageAt), {
                          addSuffix: true,
                          locale: i18n.language.startsWith("tr") ? tr : enUS,
                        })
                      : t("aiChat.new")}
                  </span>
                  <Badge variant="outline" className="text-[10px]">
                    #{conversation.id}
                  </Badge>
                </div>
                <p className="text-sm font-medium truncate">
                  {conversation.title || t("aiChat.untitledChat")}
                </p>
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-0 flex flex-col min-h-[600px]">
          {selectedConversationId ? (
            <>
              <div className="border-b p-4 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">
                    {activeConversation?.title || t("aiChat.newChat")}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {t("aiChat.coachingSession")}
                  </p>
                </div>
                {isStreaming && (
                  <Badge variant="secondary" className="text-xs animate-pulse">
                    {t("aiChat.typing")}
                  </Badge>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-muted/20">
                {isMessagesLoading ? (
                  <div className="text-sm text-muted-foreground">
                    {t("aiChat.loadingMessages")}
                  </div>
                ) : messages.length === 0 && !isStreaming ? (
                  <div className="flex h-full items-center justify-center text-center text-muted-foreground">
                    {t("aiChat.startChatPrompt")}
                  </div>
                ) : (
                  <>
                    {messages.map((msg) => {
                      const isAssistant = msg.role === "ASSISTANT";
                      return (
                        <div
                          key={msg.id}
                          className={cn(
                            "flex",
                            isAssistant ? "justify-start" : "justify-end"
                          )}
                        >
                          <div
                            className={cn(
                              "max-w-[80%] rounded-2xl px-4 py-3 text-sm shadow-sm",
                              isAssistant
                                ? "bg-card text-foreground border"
                                : "bg-primary text-primary-foreground"
                            )}
                          >
                            <div className="text-[11px] uppercase tracking-wide font-semibold mb-1 opacity-70">
                              {isAssistant ? "RituelyAI" : t("aiChat.you")}
                            </div>
                            <p className="whitespace-pre-line leading-relaxed">
                              {msg.content}
                            </p>
                            {msg.metadata?.tools && (
                              <div className="mt-2 space-y-1 border-t pt-2 opacity-80">
                                {msg.metadata.tools.map((tool, i) => (
                                  <div
                                    key={i}
                                    className="flex items-center gap-2 text-xs"
                                  >
                                    <Wrench className="h-3 w-3" />
                                    <span>
                                      {tool.name} ({tool.status})
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {isStreaming && (
                      <div className="flex justify-start">
                        <div className="max-w-[80%] rounded-2xl px-4 py-3 text-sm shadow-sm bg-card text-foreground border">
                          <div className="text-[11px] uppercase tracking-wide font-semibold mb-1 opacity-70">
                            RituelyAI
                          </div>
                          {streamingTool && (
                            <div className="text-xs text-muted-foreground italic mb-2 flex items-center gap-2">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              {t("aiChat.runningTool", { tool: streamingTool })}
                            </div>
                          )}
                          <p className="whitespace-pre-line leading-relaxed">
                            {streamingContent}
                            <span className="inline-block w-1.5 h-4 ml-1 align-middle bg-primary animate-pulse" />
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div ref={bottomRef} />
              </div>

              <form
                onSubmit={handleSendMessage}
                className="border-t p-4 flex flex-col gap-3"
              >
                <Textarea
                  placeholder={t("aiChat.placeholder")}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="min-h-[100px]"
                  disabled={isStreaming || isMessagesFetching}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                />
                <div className="flex items-center justify-end gap-2">
                  <Button
                    type="submit"
                    className="min-w-[140px]"
                    disabled={!message.trim() || isStreaming}
                  >
                    {isStreaming ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("aiChat.responding")}
                      </>
                    ) : (
                      <>
                        {t("aiChat.send")}
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center p-10 text-center">
              <Sparkles className="h-12 w-12 text-primary mb-6" />
              <h3 className="text-xl font-semibold mb-2">{t("aiChat.waitingTitle")}</h3>
              <p className="text-muted-foreground mb-6 max-w-md">
                {t("aiChat.waitingDesc")}
              </p>
              <Button
                onClick={handleCreateConversation}
                disabled={createConversationMutation.isPending}
              >
                {createConversationMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("aiChat.creating")}
                  </>
                ) : (
                  <>
                    {t("aiChat.startNewChat")}
                    <MessageSquarePlus className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </AppLayout>
  );
}
